import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { queryClient } from "@/lib/queryClient";
import { useAuthStore } from "@/store/auth";
import type { paths } from "@/types/api";

export const authKeys = {
  me: ["auth", "me"] as const,
};

type SignupBody =
  paths["/api/v1/auth/signup"]["post"]["requestBody"]["content"]["application/json"];

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: SignupBody) => {
      const { data, error } = await apiClient.POST("/api/v1/auth/signup", {
        body: vars,
      });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail ?? "Could not create your account");
      }
      return data;
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await apiClient.POST("/api/v1/auth/verify-email", {
        body: { token },
      });
      if (error || !data) throw new Error("Invalid or expired verification link");
      return data;
    },
  });
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const setOrg = useAuthStore((s) => s.setOrganizationContext);
  return useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const { data, error } = await apiClient.POST("/api/v1/auth/login", {
        body: vars,
      });
      if (error || !data) throw new Error("Invalid email or password");
      return data;
    },
    onSuccess: async (data) => {
      setSession({ token: data.access_token, refreshToken: data.refresh_token });
      const { data: me } = await apiClient.GET("/api/v1/auth/me");
      if (me?.organization_id) setOrg(me.organization_id);
      await queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: authKeys.me,
    enabled: isAuthenticated,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/auth/me");
      if (error || !data) throw new Error("Failed to load profile");
      return data;
    },
  });
}

export function useLogout() {
  const { refreshToken, clearSession } = useAuthStore.getState();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (refreshToken) {
        await apiClient.POST("/api/v1/auth/logout", {
          body: { refresh_token: refreshToken },
        });
      }
    },
    onSettled: () => {
      clearSession();
      qc.clear();
    },
  });
}

export function useAcceptInvite() {
  const setSession = useAuthStore((s) => s.setSession);
  const setOrg = useAuthStore((s) => s.setOrganizationContext);
  return useMutation({
    mutationFn: async (vars: { token: string; password: string }) => {
      const { data, error } = await apiClient.POST("/api/v1/auth/accept-invite", {
        body: vars,
      });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail ?? "This invitation link is invalid or has expired");
      }
      return data;
    },
    onSuccess: async (data) => {
      setSession({ token: data.access_token, refreshToken: data.refresh_token });
      const { data: me } = await apiClient.GET("/api/v1/auth/me");
      if (me?.organization_id) setOrg(me.organization_id);
      await queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}
